import { useApply } from '@/components/analyticsPage/tabPanels/components/drawers/indexDrawer/hooks/useApply';
import { useResetIndexQuery } from '@/components/analyticsPage/tabPanels/components/drawers/indexDrawer/hooks/useResetIndexQuery';
import IndexTab from '@/components/analyticsPage/tabPanels/components/drawers/indexDrawer/components/IndexTab';
import { useSelector, useDispatch } from 'react-redux';
import { getAnalyticsTabList } from '@/api/analytics';
import { saveAnalyticsTabList } from '@/services/analytics/Action';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnalyticsTab } from '@/types/analytics';

interface IndexDrawerProps {
  open: boolean;
  data?: any;
  action?: 'create' | 'update';
  tab?: AnalyticsTab;
  closeDrawer?: () => void;
}

const IndexDrawer = ({ open, data, tab, action = 'create', closeDrawer }: IndexDrawerProps) => {
  const [createAnalysis, updateAnalytics] = useApply();
  const [reset] = useResetIndexQuery();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const applySettings = async () => {
    setLoading(true);
    let res;
    if (action === 'create') {
      amplitude.track('analytics_analysis_create_complete', { category: 'index' });
      res = await createAnalysis();
    }
    if (action === 'update') {
      amplitude.track('analytics_analysis_update', { category: 'index' });
      res = await updateAnalytics(tab.id);
    }
    const fetchTabList = async () => {
      const results = await getAnalyticsTabList(user.userId);
      dispatch(saveAnalyticsTabList(results.data));
    };
    await fetchTabList();
    if (res) {
      navigate(`/analytics/${res.data.id}`);
    }
    setLoading(false);
    reset();
    closeDrawer();
  };

  const [t] = useTranslation('analytics');
  const [commonT] = useTranslation('common');

  return (
    <Sheet
      open={open}
      onOpenChange={() => {
        reset();
        closeDrawer();
      }}
    >
      <SheetContent className="p-0" style={{ maxWidth: '30%', minWidth: '420px' }}>
        <Tabs defaultValue="index" className="px-2 py-4 pb-12 overflow-y-auto">
          <TabsList className="w-full">
            <TabsTrigger value="index" className="w-full">
              {t('Create charts')}
            </TabsTrigger>
          </TabsList>
          <TabsContent
            value="index"
            className="max-h-[calc(100vh-30px)] overflow-y-auto pb-[100px]"
          >
            <IndexTab />
          </TabsContent>
        </Tabs>

        <div className="absolute bottom-0 right-0 flex justify-end gap-2 w-full px-4 pb-2">
          <Button variant="outline" onClick={reset}>
            <RotateCcw />
            {commonT('Reset')}
          </Button>
          <Button
            onClick={applySettings}
            className="w-full"
            disabled={
              data?.tabName?.length === 0 ||
              data?.companyId === null ||
              (data?.esgData?.dataType === 1 &&
                data?.esgData?.individualScore?.esgCode?.length === 0) ||
              data?.esgData?.to < data?.esgData?.from ||
              loading
            }
          >
            {' '}
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {commonT('Apply')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default IndexDrawer;
